(() => {
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const KLAVIYO_REVISION = '2024-02-15';
  const APPEARED_KEY_PREFIX = 'lh-popup:appeared:';
  const CART_SEEN_KEY_PREFIX = 'lh-popup:cart-seen:';

  // Let the cart drawer finish its own open animation before stacking on top of it.
  const CART_TRIGGER_DELAY_MS = 1200;
  // A popup's own add-to-cart re-broadcasts cart:refresh; don't let that reopen anything.
  const CART_TRIGGER_COOLDOWN_MS = 5000;
  const CART_EVAL_DEBOUNCE_MS = 200;

  let cartTriggerSuppressedUntil = 0;

  const themeRoutes = () => (window.themeVariables && window.themeVariables.routes) || {};
  const cartUrl = () => themeRoutes().cartUrl || '/cart';
  const cartAddUrl = () => themeRoutes().cartAddUrl || '/cart/add';
  const cartType = () =>
    (window.themeVariables && window.themeVariables.settings || {}).cartType;

  function openPopup(dialog) {
    if (!dialog || dialog.open) return;
    dialog.removeAttribute('data-closing');
    dialog.removeAttribute('data-opening');

    try {
      dialog.showModal();
    } catch (err) {
      console.error('[lh-popup] showModal failed', err);
      return;
    }

    // Flip [data-opening] next frame. The CSS animation's `from` keyframe
    // provides the starting position explicitly, so there's no dependency
    // on the browser painting the hidden state first.
    requestAnimationFrame(() => {
      dialog.setAttribute('data-opening', '');
    });

    document.documentElement.classList.add('lh-popup-lock');
    dialog.dispatchEvent(
      new CustomEvent('lh-popup:open', { bubbles: true, detail: { id: dialog.id } })
    );
  }

  function closePopup(dialog) {
    if (!dialog || !dialog.open || dialog.hasAttribute('data-closing')) return;
    const card = dialog.querySelector('.lh-popup__card');
    dialog.setAttribute('data-closing', '');
    const finalize = () => {
      dialog.removeAttribute('data-closing');
      dialog.removeAttribute('data-opening');
      try {
        dialog.close();
      } catch (_) {}
      document.documentElement.classList.remove('lh-popup-lock');
      dialog.dispatchEvent(
        new CustomEvent('lh-popup:close', { bubbles: true, detail: { id: dialog.id } })
      );
      const stepper = dialog.querySelector('lh-popup-stepper');
      if (stepper && typeof stepper.reset === 'function') {
        stepper.reset();
      }
    };
    if (!card) {
      finalize();
      return;
    }
    const onEnd = (event) => {
      if (event.target !== card) return;
      card.removeEventListener('animationend', onEnd);
      clearTimeout(safety);
      finalize();
    };
    const safety = setTimeout(() => {
      card.removeEventListener('animationend', onEnd);
      finalize();
    }, 600);
    card.addEventListener('animationend', onEnd);
  }

  function maybeAutoShow(dialog) {
    const delay = parseInt(dialog.dataset.apparitionDelay || '0', 10);
    if (!delay) return;
    const onlyOnce = dialog.hasAttribute('data-only-once');
    if (onlyOnce && localStorage.getItem(APPEARED_KEY_PREFIX + dialog.id)) return;
    setTimeout(() => {
      openPopup(dialog);
      if (onlyOnce) {
        try {
          localStorage.setItem(APPEARED_KEY_PREFIX + dialog.id, '1');
        } catch (_) {}
      }
    }, delay * 1000);
  }

  const designMode = () => !!(window.Shopify && window.Shopify.designMode);

  const anyDialogOpen = () => !!document.querySelector('dialog.lh-popup[open]');

  function cartSeenThisSession(id) {
    try {
      return !!sessionStorage.getItem(CART_SEEN_KEY_PREFIX + id);
    } catch (_) {
      return false;
    }
  }

  function markCartSeenThisSession(id) {
    try {
      sessionStorage.setItem(CART_SEEN_KEY_PREFIX + id, '1');
    } catch (_) {}
  }

  function cartMetricValue(cart, metric) {
    if (metric === 'count') return cart.item_count;
    if (metric === 'total') return cart.total_price;
    return cart.items_subtotal_price;
  }

  function numberOrNull(raw) {
    if (raw == null || String(raw).trim() === '') return null;
    const value = Number(raw);
    return Number.isNaN(value) ? null : value;
  }

  // "6000-8000" | "6000-" (no max) | "-8000" (no min) | "6000" (min only)
  function parseRange(raw) {
    const text = String(raw || '').trim();
    if (!text) return null;
    const [minPart, maxPart] = text.includes('-') ? text.split('-') : [text, ''];
    const min = numberOrNull(minPart);
    const max = numberOrNull(maxPart);
    if (min === null && max === null) return null;
    return { min, max };
  }

  function parseCurrencyList(raw) {
    return String(raw || '')
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);
  }

  function parseRangeOverrides(raw) {
    const overrides = {};
    for (const entry of String(raw || '').split(',')) {
      const text = entry.trim();
      const separator = text.indexOf(':');
      if (separator < 0) continue;
      const code = text.slice(0, separator).trim().toUpperCase();
      const range = parseRange(text.slice(separator + 1));
      if (code && range) overrides[code] = range;
    }
    return overrides;
  }

  function cartCurrency(cart) {
    const raw = (cart.currency && cart.currency.iso_code) || cart.currency;
    return String(raw || '').toUpperCase();
  }

  function cartQualifies(dialog, cart) {
    const currency = cartCurrency(cart);

    const allowed = parseCurrencyList(dialog.dataset.cartCurrencies);
    if (allowed.length && !allowed.includes(currency)) return false;

    const value = cartMetricValue(cart, dialog.dataset.cartMetric);
    if (typeof value !== 'number' || Number.isNaN(value)) return false;

    const overrides = parseRangeOverrides(dialog.dataset.cartOverrides);
    const range = overrides[currency] || {
      min: numberOrNull(dialog.dataset.cartMin),
      max: numberOrNull(dialog.dataset.cartMax),
    };

    if (range.min !== null && value < range.min) return false;
    if (range.max !== null && value > range.max) return false;
    return true;
  }

  function evaluateCartTriggers(cart) {
    if (!cart || designMode()) return;
    if (anyDialogOpen()) return;
    if (Date.now() < cartTriggerSuppressedUntil) return;

    const dialogs = document.querySelectorAll('dialog.lh-popup[data-cart-trigger]');
    for (const dialog of dialogs) {
      if (!dialog.id || cartSeenThisSession(dialog.id)) continue;

      // An offer step whose products are all sold out has nothing to show.
      const firstStep = dialog.querySelector('.lh-popup__step');
      if (firstStep && firstStep.querySelector('[data-offer-unavailable]')) continue;

      if (!cartQualifies(dialog, cart)) continue;

      markCartSeenThisSession(dialog.id);
      setTimeout(() => {
        if (anyDialogOpen()) return;
        if (Date.now() < cartTriggerSuppressedUntil) return;
        openPopup(dialog);
      }, CART_TRIGGER_DELAY_MS);
      return;
    }
  }

  let cartEvalTimer = null;

  function onCartChanged(event) {
    if (!document.querySelector('dialog.lh-popup[data-cart-trigger]')) return;

    const detailCart = event && event.detail && event.detail.cart;
    clearTimeout(cartEvalTimer);
    cartEvalTimer = setTimeout(async () => {
      let cart = detailCart;
      if (!cart) {
        try {
          cart = await fetch(`${cartUrl()}.js`).then((res) => res.json());
        } catch (err) {
          console.error('[lh-popup] cart fetch failed', err);
          return;
        }
      }
      evaluateCartTriggers(cart);
    }, CART_EVAL_DEBOUNCE_MS);
  }

  document.documentElement.addEventListener('cart:updated', onCartChanged);
  document.documentElement.addEventListener('cart:refresh', onCartChanged);

  function initDialog(dialog) {
    if (dialog.__lhPopupInit) return;
    dialog.__lhPopupInit = true;

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      if (designMode()) return;
      closePopup(dialog);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-action="close"]')) {
        event.preventDefault();
        closePopup(dialog);
        return;
      }
      if (designMode()) return;
      if (event.target === dialog) {
        const rect = dialog.querySelector('.lh-popup__card')?.getBoundingClientRect();
        if (!rect) return closePopup(dialog);
        const x = event.clientX;
        const y = event.clientY;
        const inside =
          x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        if (!inside) closePopup(dialog);
      }
    });

    if (!designMode()) maybeAutoShow(dialog);
  }

  function initAllDialogs(root = document) {
    root.querySelectorAll('dialog.lh-popup').forEach(initDialog);
  }

  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a[href^="#"]');
    if (!anchor) return;
    const id = anchor.getAttribute('href').slice(1);
    if (!id) return;
    const dialog = document.getElementById(id);
    if (!dialog || !dialog.classList.contains('lh-popup')) return;
    event.preventDefault();
    openPopup(dialog);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllDialogs());
  } else {
    initAllDialogs();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllDialogs(event.target);
  });

  if (designMode()) {
    const findDialog = (target) =>
      target.querySelector?.('dialog.lh-popup') ||
      target.closest?.('dialog.lh-popup');

    document.addEventListener('shopify:section:select', (event) => {
      const dialog = findDialog(event.target);
      if (dialog) openPopup(dialog);
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const dialog = findDialog(event.target);
      if (dialog) closePopup(dialog);
    });

    document.addEventListener('shopify:block:select', (event) => {
      const step = event.target.closest?.('.lh-popup__step');
      if (!step) return;
      const dialog = step.closest('dialog.lh-popup');
      const stepper = step.closest('lh-popup-stepper');
      if (dialog) openPopup(dialog);
      if (stepper && step.dataset.stepId) {
        requestAnimationFrame(() => stepper.goTo(step.dataset.stepId));
      }
    });
  }

  const STEP_EXIT_MS = 140;
  const STEP_ENTER_MS = 240;
  const HEIGHT_TRANSITION_MS = 280;
  const ENTER_DELAY_MS = 140; // ms after resize starts before entrance begins
  const HEIGHT_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  class LhPopupStepper extends HTMLElement {
    constructor() {
      super();
      this._properties = {};
      this._transitioning = false;
    }

    connectedCallback() {
      this.popupId = this.dataset.popupId;
      this.companyId = this.dataset.klaviyoCompanyId;
      this.listId = this.dataset.klaviyoListId;
      this.customSource = this.dataset.klaviyoCustomSource || 'Popup';
      this._onClickBound = this._onClick.bind(this);
      this._onSubmitBound = this._onSubmit.bind(this);
      this.addEventListener('click', this._onClickBound);
      this.addEventListener('submit', this._onSubmitBound);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClickBound);
      this.removeEventListener('submit', this._onSubmitBound);
    }

    _dialog() {
      return this.closest('dialog.lh-popup');
    }

    _currentStep() {
      return this.querySelector('.lh-popup__step[data-active]');
    }

    async goTo(stepId) {
      if (!stepId || this._transitioning) return;
      const next = this.querySelector(
        `.lh-popup__step[data-step-id="${CSS.escape(stepId)}"]`
      );
      if (!next) return;
      const current = this._currentStep();
      if (current === next) return;

      const emit = () =>
        this.dispatchEvent(
          new CustomEvent('lh-popup:step', {
            bubbles: true,
            detail: { popupId: this.popupId, stepId },
          })
        );

      if (!current || prefersReducedMotion()) {
        current?.removeAttribute('data-active');
        next.setAttribute('data-active', '');
        emit();
        return;
      }

      const card = this.closest('.lh-popup__card');
      const startHeight = card?.offsetHeight || 0;

      this._transitioning = true;
      try {
        if (card) {
          card.style.height = startHeight + 'px';
        }

        // Phase 1 — exit: fade current out (no blur).
        current.setAttribute('data-exiting', '');
        await sleep(STEP_EXIT_MS);
        current.removeAttribute('data-active');
        current.removeAttribute('data-exiting');

        // Mount next step invisible + blurred.
        next.setAttribute('data-entering', '');
        next.setAttribute('data-active', '');
        void next.offsetHeight;

        // Phase 2 — resize card to fit next step.
        if (card) {
          card.style.height = '';
          const endHeight = card.offsetHeight;
          card.style.height = startHeight + 'px';
          void card.offsetHeight;
          card.style.transition = `height ${HEIGHT_TRANSITION_MS}ms ${HEIGHT_EASING}`;
          card.style.height = endHeight + 'px';
        }

        // Phase 3 — a beat into the resize, fade + unblur the next step in.
        await sleep(ENTER_DELAY_MS);
        requestAnimationFrame(() => next.removeAttribute('data-entering'));

        const remainingResize = Math.max(HEIGHT_TRANSITION_MS - ENTER_DELAY_MS, 0);
        await sleep(Math.max(remainingResize, STEP_ENTER_MS));

        if (card) {
          card.style.transition = '';
          card.style.height = '';
        }
        emit();
      } finally {
        this._transitioning = false;
      }
    }

    reset() {
      this._properties = {};
      this._transitioning = false;
      const card = this.closest('.lh-popup__card');
      if (card) {
        card.style.transition = '';
        card.style.height = '';
      }
      const steps = this.querySelectorAll('.lh-popup__step');
      steps.forEach((step, index) => {
        step.removeAttribute('data-exiting');
        step.removeAttribute('data-entering');
        if (index === 0) step.setAttribute('data-active', '');
        else step.removeAttribute('data-active');
      });
      this.querySelectorAll('form.lh-popup__form-inner').forEach((form) => {
        form.reset();
        this._hideBanner(form);
      });
      this.querySelectorAll('[data-offer-error]').forEach((el) =>
        this._hideOfferError(el)
      );
    }

    _onClick(event) {
      const offer = event.target.closest('[data-offer-add]');
      if (offer && this.contains(offer)) {
        this._addOffer(offer);
        return;
      }
      const choice = event.target.closest('[data-step-choice]');
      if (choice && this.contains(choice)) {
        const { propertyKey, propertyValue } = choice.dataset;
        if (propertyKey) {
          this._properties[propertyKey] =
            propertyValue || choice.textContent.trim();
        }
        const current = this._currentStep();
        const next = choice.dataset.next || current?.dataset.nextStep;
        if (next) this.goTo(next);
        return;
      }
      const action = event.target.closest('[data-step-action]');
      if (action && this.contains(action)) {
        const next = action.dataset.next;
        if (next === '__close') {
          const dialog = this._dialog();
          if (dialog) closePopup(dialog);
        } else if (next) {
          this.goTo(next);
        }
      }
    }

    async _addOffer(button) {
      if (button.hasAttribute('aria-busy')) return;

      const items = (button.dataset.offerItems || '')
        .split(',')
        .map((pair) => pair.split(':'))
        .filter((parts) => parts.length === 2)
        .map(([id, quantity]) => ({ id: Number(id), quantity: Number(quantity) || 1 }))
        .filter((item) => item.id > 0);
      if (!items.length) return;

      const wrapper = button.closest('.lh-popup__offer');
      const errorEl = wrapper?.querySelector('[data-offer-error]');
      this._hideOfferError(errorEl);
      button.setAttribute('aria-busy', 'true');

      try {
        const addRes = await fetch(`${cartAddUrl()}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!addRes.ok) {
          let detail = null;
          try {
            const body = await addRes.json();
            detail = body.description || body.message || null;
          } catch (_) {
            // non-JSON error body
          }
          throw new Error(detail || '');
        }

        let cart = await fetch(`${cartUrl()}.js`).then((res) => res.json());
        cart = await this._applyOfferDiscount(cart, button.dataset.offerDiscount);

        cartTriggerSuppressedUntil = Date.now() + CART_TRIGGER_COOLDOWN_MS;

        this.dispatchEvent(
          new CustomEvent('lh-popup:offer-added', {
            bubbles: true,
            detail: {
              popupId: this.popupId,
              stepId: button.closest('.lh-popup__step')?.dataset.stepId,
              items,
              discountCode: button.dataset.offerDiscount || null,
              cart,
            },
          })
        );

        const dialog = this._dialog();
        if (dialog) closePopup(dialog);

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart, openMiniCart: cartType() === 'drawer' },
          })
        );
      } catch (err) {
        console.error('[lh-popup] offer add failed', err);
        this._showOfferError(
          errorEl,
          err.message || button.dataset.errorMessage
        );
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    // `discount` replaces the cart's whole code set, so merge rather than overwrite.
    // A rejected code is not worth undoing a successful add over.
    async _applyOfferDiscount(cart, code) {
      if (!code) return cart;

      const codes = (cart.discount_codes || [])
        .map((entry) => entry.code)
        .filter(Boolean);
      if (!codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
        codes.push(code);
      }

      try {
        const res = await fetch(`${cartUrl()}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') }),
        });
        if (res.ok) return await res.json();
        console.warn('[lh-popup] discount code rejected', code);
      } catch (err) {
        console.warn('[lh-popup] discount code failed', err);
      }
      return cart;
    }

    _showOfferError(el, message) {
      if (!el) return;
      el.textContent = message || '';
      el.classList.add('is-visible', 'is-error');
    }

    _hideOfferError(el) {
      el?.classList.remove('is-visible', 'is-error');
    }

    async _onSubmit(event) {
      const form = event.target.closest('.lh-popup__form-inner');
      if (!form || !this.contains(form)) return;
      event.preventDefault();

      const emailInput = form.querySelector('input[type="email"]');
      const email = emailInput?.value?.trim();
      if (!email) return;

      const properties = { ...this._properties };
      if (form.dataset.extraPropertyKey) {
        properties[form.dataset.extraPropertyKey] =
          form.dataset.extraPropertyValue || '';
      }

      const submit = form.querySelector('.lh-popup__submit');
      submit?.setAttribute('aria-busy', 'true');
      this._hideBanner(form);

      const useKlaviyo = this.companyId && this.listId;

      try {
        const { ok, errorMessage } = useKlaviyo
          ? await this._submitKlaviyo(email, properties)
          : await this._submitShopify(email, properties);

        if (ok) {
          this.dispatchEvent(
            new CustomEvent('lh-popup:submitted', {
              bubbles: true,
              detail: {
                popupId: this.popupId,
                email,
                properties,
                provider: useKlaviyo ? 'klaviyo' : 'shopify',
              },
            })
          );
          const successStep = form.dataset.successStep;
          if (successStep) {
            this.goTo(successStep);
          } else {
            this._showBanner(form, 'success', form.dataset.successMessage);
          }
          form.reset();
          this._properties = {};
        } else {
          this._showBanner(
            form,
            'error',
            errorMessage || form.dataset.errorMessage
          );
        }
      } catch (err) {
        console.error('[lh-popup] submit failed', err);
        this._showBanner(form, 'error', form.dataset.errorMessage);
      } finally {
        submit?.removeAttribute('aria-busy');
      }
    }

    async _submitKlaviyo(email, properties) {
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.customSource,
            profile: {
              data: {
                type: 'profile',
                attributes: { email, properties },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: this.listId } },
          },
        },
      };

      const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(
        this.companyId
      )}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) return { ok: true };

      let errorMessage = null;
      try {
        const body = await res.json();
        errorMessage = body?.errors?.[0]?.detail || null;
      } catch (_) {
        // non-JSON error body
      }
      return { ok: false, errorMessage };
    }

    async _submitShopify(email, properties) {
      const tagParts = Object.values(properties)
        .filter((v) => typeof v === 'string' && v.trim().length)
        .map((v) => v.trim().replace(/,/g, ' '));
      if (this.customSource) tagParts.push(this.customSource);
      tagParts.push('newsletter');

      const body = new URLSearchParams();
      body.set('form_type', 'customer');
      body.set('utf8', '✓');
      body.set('contact[email]', email);
      body.set('contact[accepts_marketing]', 'true');
      body.set('contact[tags]', tagParts.join(','));

      const res = await fetch('/contact#contact_form', {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/html;q=0.8',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      });

      // Shopify's /contact returns 200 for both success and validation errors
      // (error text rendered in HTML). Client-side `type=email` + `required`
      // catches invalid emails before we get here, so treat 2xx as success.
      return { ok: res.ok };
    }

    _showBanner(form, kind, message) {
      const banner = form.querySelector('[data-banner]');
      if (!banner) return;
      banner.textContent = message || '';
      banner.classList.remove('is-success', 'is-error');
      banner.classList.add('is-visible', `is-${kind}`);
    }

    _hideBanner(form) {
      const banner = form.querySelector('[data-banner]');
      banner?.classList.remove('is-visible', 'is-success', 'is-error');
    }
  }

  if (!customElements.get('lh-popup-stepper')) {
    customElements.define('lh-popup-stepper', LhPopupStepper);
  }

  window.LhPopup = Object.freeze({ open: openPopup, close: closePopup });
})();
